// Rebuild a small, inert HTML vocabulary. Never reuse attributes from pasted
// markup or imported question files, except explicitly validated attributes.
export const MAX_FEEDBACK_LENGTH = 2 * 1024 * 1024;
const tags = new Set(['P', 'DIV', 'SPAN', 'BR', 'STRONG', 'B', 'EM', 'I', 'U', 'S', 'STRIKE', 'UL', 'OL', 'LI', 'BLOCKQUOTE', 'H2', 'H3', 'H4']);
const fontSizes = ['10px', '13px', '16px', '18px', '24px', '32px', '48px'];

function copyTextStyle(node, target) {
    for (const property of ['color', 'background-color']) {
        const value = node.style.getPropertyValue(property);
        if (/^(#[0-9a-f]{3,8}|rgba?\([\d\s.,%]+\)|[a-z]+)$/i.test(value)) target.style.setProperty(property, value);
    }
    const size = node.style.fontSize;
    if (/^\d+(\.\d+)?px$/.test(size) && parseFloat(size) >= 10 && parseFloat(size) <= 72) target.style.fontSize = size;
    if (['left', 'center', 'right', 'justify'].includes(node.style.textAlign)) target.style.textAlign = node.style.textAlign;
    if (['bold', 'normal', '700', '400'].includes(node.style.fontWeight)) target.style.fontWeight = node.style.fontWeight;
    if (['italic', 'normal'].includes(node.style.fontStyle)) target.style.fontStyle = node.style.fontStyle;
    if (['underline', 'line-through', 'none'].includes(node.style.textDecorationLine)) target.style.textDecorationLine = node.style.textDecorationLine;
}
const discard = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'SVG', 'MATH', 'TEMPLATE', 'FORM', 'INPUT', 'BUTTON', 'TEXTAREA', 'SELECT']);

export function safeLinkHref(value) {
    const href = String(value || '').trim();
    if (/\s/.test(href)) return '';
    try {
        const url = new URL(href);
        if (['https:', 'http:', 'mailto:'].includes(url.protocol) && !url.username && !url.password) return url.href;
    } catch { /* Not an absolute link. */ }
    return '';
}

export function safeImageSource(value) {
    const src = String(value || '').trim();
    if (/^data:image\/(png|jpeg|webp|gif);base64,[a-z0-9+/]+=*$/i.test(src)) return src;
    // Only local absolute paths and HTTPS images; no protocol-relative URLs.
    if (/^\/(?![\/\\])[^\s\\]*$/.test(src)) return src;
    try {
        const url = new URL(src);
        if (url.protocol === 'https:' && !url.username && !url.password) return url.href;
    } catch { /* Invalid/relative URL. */ }
    return '';
}

export function sanitizeFeedback(value) {
    if (typeof value !== 'string' || value.length > MAX_FEEDBACK_LENGTH) return '';
    const source = document.createElement('template');
    source.innerHTML = value;
    const output = document.createElement('div');
    function copy(node, parent, depth = 0) {
        if (depth > 40) return;
        if (node.nodeType === 3) {
            parent.appendChild(document.createTextNode(node.textContent));
            return;
        }
        if (node.nodeType !== 1 || discard.has(node.tagName)) return;
        if (node.tagName === 'IMG') {
            const src = safeImageSource(node.getAttribute('src'));
            if (!src) return;
            const image = document.createElement('img');
            image.setAttribute('src', src);
            image.setAttribute('alt', (node.getAttribute('alt') || 'Imagen del feedback').slice(0, 300));
            const width = node.getAttribute('width');
            if (/^\d{1,4}$/.test(width || '') && Number(width) >= 32 && Number(width) <= 1600) image.setAttribute('width', String(Number(width)));
            const align = node.getAttribute('data-align');
            if (['left', 'center', 'right'].includes(align)) image.setAttribute('data-align', align);
            parent.appendChild(image);
            return;
        }
        let target = tags.has(node.tagName) ? document.createElement(node.tagName.toLowerCase()) : parent;
        if (node.tagName === 'FONT') {
            target = document.createElement('span');
            const color = document.createElement('span');
            color.style.color = node.getAttribute('color') || '';
            copyTextStyle(color, target);
            const size = Number(node.getAttribute('size'));
            if (Number.isInteger(size) && size >= 1 && size <= 7) target.style.fontSize = fontSizes[size - 1];
        }
        if (node.tagName === 'A') {
            const href = safeLinkHref(node.getAttribute('href'));
            if (href) {
                target = document.createElement('a');
                target.setAttribute('href', href);
                const linkTarget = node.getAttribute('target') === '_self' ? '_self' : '_blank';
                target.setAttribute('target', linkTarget);
                if (linkTarget === '_blank') target.setAttribute('rel', 'noopener noreferrer');
            }
        }
        if (target !== parent) {
            copyTextStyle(node, target);
            if (node.tagName === 'DIV' && node.getAttribute('data-marquee') === 'true') target.setAttribute('data-marquee', 'true');
            if (['SPAN', 'DIV'].includes(node.tagName) && node.getAttribute('data-marquee-text') === 'true') target.setAttribute('data-marquee-text', 'true');
            parent.appendChild(target);
        }
        for (const child of node.childNodes) copy(child, target, depth + 1);
    }
    for (const child of source.content.childNodes) copy(child, output);
    return output.textContent.trim() || output.querySelector('img') ? output.innerHTML : '';
}

export async function prepareFeedbackImage(file) {
    if (!/^image\/(png|jpeg|webp|gif)$/.test(file.type)) throw new Error('Selecciona una imagen PNG, JPG, WebP o GIF.');
    if (file.size > 10 * 1024 * 1024) throw new Error('La imagen no puede superar 10 MB.');
    const url = URL.createObjectURL(file);
    try {
        const image = new Image();
        image.src = url;
        await image.decode();
        const ratio = Math.min(1, 1600 / Math.max(image.naturalWidth, image.naturalHeight));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(image.naturalWidth * ratio));
        canvas.height = Math.max(1, Math.round(image.naturalHeight * ratio));
        canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
        const src = canvas.toDataURL('image/webp', 0.85);
        if (src.length > 1024 * 1024) throw new Error('La imagen sigue siendo demasiado grande. Prueba una más pequeña.');
        return src;
    } finally {
        URL.revokeObjectURL(url);
    }
}
